import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError, fmtDate } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Users, Truck, ClipboardList, ShieldCheck, Wrench, AlertTriangle, HardHat, User, ChevronRight, KeyRound } from "lucide-react";

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="card p-4 border-t-4" style={{ borderTopColor: accent }} data-testid={`fleet-stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <Icon className="w-4 h-4 mb-2" style={{ color: accent }} />
      <p className="font-mono text-3xl" style={{ color: accent }}>{value}</p>
      <p className="font-mono text-[10px] uppercase tracking-widest text-[#94A3B8] mt-1">{label}</p>
    </div>
  );
}

export default function AdminFleetDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [d, setD] = useState(null);

  useEffect(() => {
    api.get(`/admin/fleets/${id}`).then(({ data }) => setD(data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail) || "Could not load fleet"));
  }, [id]);

  if (!d) return <div className="p-5 md:p-10" data-testid="admin-fleet-page"><p className="font-head text-[#FFB800] uppercase tracking-widest">Loading…</p></div>;

  const { fleet, stats, members, machines } = d;

  return (
    <div className="p-5 md:p-10 pb-24 max-w-5xl" data-testid="admin-fleet-page">
      <button onClick={() => nav("/admin")} className="flex items-center gap-2 text-[#94A3B8] font-body text-sm mb-4" data-testid="fleet-back"><ArrowLeft className="w-4 h-4" /> Admin</button>

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div>
          <h1 className="font-head font-900 uppercase tracking-tight text-3xl">{fleet.name}</h1>
          <p className="font-mono text-xs text-[#94A3B8]">{fleet.owner_email} · created {fmtDate(fleet.created_at)}</p>
        </div>
        <span className={`badge ${fleet.comp ? "badge-upcoming" : "badge-healthy"}`}>{fleet.plan}</span>
        <span className="font-mono text-xs text-[#94A3B8] flex items-center gap-1.5"><KeyRound className="w-3.5 h-3.5" /> {fleet.invite_code}</span>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 mb-6">
        <Stat icon={Users} label="Members" value={stats.members} accent="#3B82F6" />
        <Stat icon={Truck} label="Machines" value={stats.machines} accent="#A855F7" />
        <Stat icon={AlertTriangle} label="Flagged" value={stats.flagged} accent="#FF3B30" />
        <Stat icon={ClipboardList} label="Open Jobs" value={stats.work_orders_open} accent="#FFB800" />
        <Stat icon={ShieldCheck} label="Pre-Starts" value={stats.prestarts} accent="#22C55E" />
        <Stat icon={Wrench} label="Services" value={stats.services} accent="#0EA5E9" />
      </div>

      <div className="card mb-4" data-testid="fleet-members">
        <div className="px-5 py-4 border-b border-white/10"><h2 className="font-head font-700 text-lg uppercase tracking-wide">Members ({members.length})</h2></div>
        <div className="divide-y divide-white/10">
          {members.map((m) => (
            <button key={m.id} onClick={() => nav(`/admin/users/${m.id}`)} className="w-full text-left flex items-center gap-4 px-5 py-4 group hover:bg-[#1A1C23] transition-colors cursor-pointer" data-testid={`fleet-member-${m.id}`}>
              <div className="w-10 h-10 bg-[#0D0E11] border border-white/10 flex items-center justify-center">
                {m.role === "manager" ? <HardHat className="w-5 h-5 text-[#FFB800]" /> : <User className="w-5 h-5 text-[#94A3B8]" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-head font-700 text-lg truncate flex items-center gap-1.5 group-hover:text-[#FFB800] transition-colors">{m.name} {m.is_owner && <span className="badge badge-upcoming">owner</span>}<ChevronRight className="w-4 h-4 text-[#94A3B8] group-hover:text-[#FFB800]" /></p>
                <p className="font-mono text-xs text-[#94A3B8] truncate">{m.email} · joined {fmtDate(m.created_at)}</p>
              </div>
              <span className={`badge ${m.role === "manager" ? "badge-upcoming" : "badge-healthy"}`}>{m.role}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="card" data-testid="fleet-machines">
        <div className="px-5 py-4 border-b border-white/10"><h2 className="font-head font-700 text-lg uppercase tracking-wide">Machines ({machines.length})</h2></div>
        {machines.length === 0 ? <p className="p-5 font-body text-sm text-[#94A3B8]">No machines in this fleet.</p> : (
          <div className="divide-y divide-white/10">
            {machines.map((m) => (
              <button key={m.id} onClick={() => nav(`/admin/machines/${m.id}`)} className="w-full text-left px-5 py-3 flex items-center gap-3 group hover:bg-[#1A1C23] transition-colors cursor-pointer" data-testid={`fleet-machine-${m.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="font-head font-700 uppercase truncate flex items-center gap-1.5 group-hover:text-[#FFB800] transition-colors">{m.fleet_number ? `${m.fleet_number} · ` : ""}{m.make} {m.model}<ChevronRight className="w-4 h-4 text-[#94A3B8] group-hover:text-[#FFB800]" /></p>
                  <p className="font-mono text-[11px] text-[#94A3B8]">{(m.type || "other").toUpperCase()}{m.rego ? ` · ${m.rego}` : ""} · {m.hours || 0}h</p>
                </div>
                {m.flagged && <span className="badge badge-overdue flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Flagged</span>}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
